import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    Add,
    MoreVert,
    Delete,
    Edit,
    Send,
    Schedule,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ResponsiveTable from '../components/ResponsiveTable';
import { getScheduledPosts, deleteScheduledPost, postNow } from '../redux/slices/scheduledPostSlice';

const ScheduledPosts = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { posts, loading } = useSelector((state) => state.scheduledPosts);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedPost, setSelectedPost] = useState(null);

    useEffect(() => {
        dispatch(getScheduledPosts());
    }, [dispatch]);

    const handleMenuOpen = (event, post) => {
        setAnchorEl(event.currentTarget);
        setSelectedPost(post);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedPost(null);
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this scheduled post?')) {
            await dispatch(deleteScheduledPost(selectedPost._id));
            handleMenuClose();
        }
    };

    const handlePostNow = async () => {
        if (window.confirm('Post this content immediately?')) {
            await dispatch(postNow(selectedPost._id));
            handleMenuClose();
        }
    };

    const getPlatformColor = (platform) => {
        const colors = {
            whatsapp: '#25D366',
            telegram: '#0088cc',
            instagram: '#E4405F',
            facebook: '#1877F2',
        };
        return colors[platform] || '#757575';
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold">
                    المنشورات المجدولة
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate('/campaigns')} // Assuming new posts start from campaigns or similar
                >
                    جدولة منشور جديد
                </Button>
            </Box>

            <Card>
                <CardContent>
                    <ResponsiveTable
                        headers={[
                            { key: 'content', label: 'المحتوى' },
                            { key: 'platform', label: 'المنصة' },
                            { key: 'scheduledTime', label: 'وقت النشر' },
                            { key: 'status', label: 'الحالة' },
                            { key: 'actions', label: 'الإجراءات' },
                        ]}
                        rows={posts}
                        desktopRowRenderer={(post) => (
                            <tr key={post._id} style={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                                <td style={{ padding: '16px', maxWidth: '300px' }}>
                                    <Typography noWrap>{post.content?.text || post.caption}</Typography>
                                </td>
                                <td style={{ padding: '16px' }}>
                                    <Chip
                                        icon={<Schedule sx={{ fontSize: '1rem !important' }} />}
                                        label={post.platform}
                                        sx={{
                                            bgcolor: getPlatformColor(post.platform),
                                            color: 'white',
                                            textTransform: 'capitalize'
                                        }}
                                        size="small"
                                    />
                                </td>
                                <td style={{ padding: '16px' }}>
                                    {new Date(post.scheduledTime).toLocaleString('ar-EG')}
                                </td>
                                <td style={{ padding: '16px' }}>
                                    <Chip
                                        label={post.status === 'completed' ? 'تم النشر' : post.status === 'failed' ? 'فشل' : 'مجدول'}
                                        color={post.status === 'completed' ? 'success' : post.status === 'failed' ? 'error' : 'warning'}
                                        size="small"
                                    />
                                </td>
                                <td style={{ padding: '16px' }}>
                                    <IconButton onClick={(e) => handleMenuOpen(e, post)}>
                                        <MoreVert />
                                    </IconButton>
                                </td>
                            </tr>
                        )}
                        mobileCardRenderer={(post) => (
                            <Card key={post._id} sx={{ mb: 2, border: '1px solid #eee' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                        <Chip
                                            label={post.platform}
                                            sx={{ bgcolor: getPlatformColor(post.platform), color: 'white' }}
                                            size="small"
                                        />
                                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, post)}>
                                            <MoreVert />
                                        </IconButton>
                                    </Box>
                                    <Typography variant="body1" gutterBottom>
                                        {post.content?.text || post.caption}
                                    </Typography>
                                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(post.scheduledTime).toLocaleString('ar-EG')}
                                        </Typography>
                                        <Chip
                                            label={post.status === 'completed' ? 'تم النشر' : 'مجدول'}
                                            color={post.status === 'completed' ? 'success' : 'warning'}
                                            size="small"
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        )}
                    />
                </CardContent>
            </Card>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handlePostNow}>
                    <ListItemIcon><Send fontSize="small" /></ListItemIcon>
                    <ListItemText>نشر الآن</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                    <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>حذف</ListItemText>
                </MenuItem>
            </Menu>
        </Container>
    );
};

export default ScheduledPosts;
